import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { Stack, Callout, TextField, DirectionalHint, DefaultButton, PrimaryButton, IconButton } from '@fluentui/react';
import { useDefaultStyles } from '@bingads-webui-react/with-default-styles';

import { iconClassNames, getStyles } from '../../style/index';

function valiadate(name) {
  if (name.trim() === '') {
    return _TL_('Required field');
  }
  if (name.length > 85) {
    return _TL_('Field should not exceed 85 characters');
  }
  return '';
}

export function NameEditor(props) {
  const {
    value, reportAction, i18n, onNameChange, onSave, onSaveAs, isDirty, isLegacyReport, shouldHideThisFunctionality,
  } = props;
  const [editName, setEditName] = useState(value || '');
  const classes = useDefaultStyles(getStyles);
  const editIcon = { iconName: 'Edit' };
  const saveIcon = { iconName: 'Save' };
  const saveAsIcon = { iconName: 'SaveAs' };

  const [editNamePromptVisibleStatus, updateEditNamePromptVisible] = useState(0);// status be like: 0: none display 1:in edit status 2:in save status

  useEffect(() => {
    // It takes time to load reportParams, so the default value of editname is set to an empty string. Later on reportParams loaded, we need to sync to editName, otherwise validation will fail due to editName empty.
    setEditName(value);
  }, [value]);

  function showEditNamePrompt() {
    updateEditNamePromptVisible(1);
  }

  function showSaveNamePrompt() {
    updateEditNamePromptVisible(2);
  }

  function closeNameEditPrompt() {
    updateEditNamePromptVisible(0);
  }

  function handleSubmit() {
    if (valiadate(editName)) {
      return;
    }
    if (editNamePromptVisibleStatus === 1) {
      onNameChange(editName);
    } else {
      onSaveAs(editName);
    }
    closeNameEditPrompt();
  }

  function onClickSave() {
    onSave();
  }

  const error = i18n.getString(valiadate(editName));

  return (
    <React.Fragment>
      {value &&
        <Stack className="name-editor-container" horizontal verticalAlign="center" tokens={{ childrenGap: 15 }}>
          <span id="ReportName" className="name" title={value}>{value}</span>
          { !shouldHideThisFunctionality &&
          <div className="name-editor-buttons">
            <IconButton
              id="EditIcon"
              iconProps={editIcon}
              title={i18n.getString(_TL_('Edit'))}
              ariaLabel={i18n.getString(_TL_('Edit'))}
              disabled={false}
              checked={false}
              className={`${iconClassNames.blue} ${classes.icons}`}
              onClick={showEditNamePrompt}
            />
            {reportAction === 'edit' &&
              <IconButton
                id="SaveIcon"
                iconProps={saveIcon}
                title={isLegacyReport ?
                  i18n.getString(_TL_('You can currently only save this report as a new report.')) :
                  i18n.getString(_TL_('Save'))}
                ariaLabel={i18n.getString(_TL_('Save'))}
                disabled={!isDirty || isLegacyReport}
                checked={false}
                className={`${iconClassNames.blue} ${classes.icons}`}
                onClick={onClickSave}
              />}
            <IconButton
              id="SaveAsIcon"
              iconProps={saveAsIcon}
              title={i18n.getString(_TL_('Save as'))}
              ariaLabel={i18n.getString(_TL_('Save as'))}
              disabled={false}
              checked={false}
              className={`${iconClassNames.blue} ${classes.icons}`}
              onClick={showSaveNamePrompt}
            />
          </div>}
          {editNamePromptVisibleStatus &&
            <div className={classes.reportPreviewCalloutContainer}>
              <Callout
                target={editNamePromptVisibleStatus === 1 ? '#EditIcon' : '#SaveAsIcon'}
                setInitialFocus
                gapSpace={14}
                preventDismissOnScroll
                isBeakVisible={false}
                directionalHint={DirectionalHint.bottomLeftEdge}
                onDismiss={closeNameEditPrompt}
                className={`${classes.reportPreviewCallout} name-editor-callout`}
              >
                <span className={classes.reportPreviewCalloutTitle}>
                  {editNamePromptVisibleStatus === 1 ? i18n.getString(_TL_('Rename this report:')) : i18n.getString(_TL_('Save as customized report'))}
                </span>
                <TextField
                  className={classes.reportPreviewNameInput}
                  defaultValue={value}
                  errorMessage={error}
                  onChange={(e, v) => setEditName(v)}
                />
                <Stack horizontal gap={8} className={classes.reportPreviewNameEditActions}>
                  <PrimaryButton onClick={handleSubmit} text={editNamePromptVisibleStatus === 1 ? i18n.getString(_TL_('Apply')) : i18n.getString(_TL_('Save'))} />
                  <DefaultButton onClick={closeNameEditPrompt} text={i18n.getString(_TL_('Cancel'))} />
                </Stack>
              </Callout>
            </div>
          }
        </Stack>}
    </React.Fragment>
  );
}


NameEditor.propTypes = {
  value: PropTypes.string.isRequired,
  reportAction: PropTypes.string.isRequired,
  i18n: PropTypes.shape({}).isRequired,
  onNameChange: PropTypes.func.isRequired,
  onSave: PropTypes.func.isRequired,
  onSaveAs: PropTypes.func.isRequired,
  isDirty: PropTypes.bool.isRequired,
  isLegacyReport: PropTypes.bool.isRequired,
};
